import { EventMeta } from '../../../interfaces/meta.interface';

type SiteCollectionAdminAddedFields = {
    ObjectId: string;
};

const SiteCollectionAdminAdded: EventMeta<SiteCollectionAdminAddedFields> = {
    title: 'SharePoint Site Collection Admin Added',
    default: {
        description: 'SharePoint Site Collection Admin Added',
        longDescription:
            'A person was added as a site collection administrator. Site collection administrators have full permissions for the site collection and all subsites.',
        severity: 'info',
    },
    fieldsExtractor: (log: any) => {
        return {
            ObjectId: log.ObjectId,
        };
    },
    classifications: [
        // site admin added: fraudulent IP. not in allowed list.
        {
            severity: 'danger',
            description: (fields) => `A suspicious IP added a site collection administrator`,
            longDescription: (fields) =>
                `A person was added as a SharePoint site collection administrator from a suspicious IP address that is not in your allowed list. This activity may indicate an attempt to gain unauthorized administrative access.`,
            predicates: {
                isAllowedASN: false,
                isAllowedIP: false,
                isAllowedRegion: false,
                isAllowedCountry: false,
                isFraudulentIP: true,
                isMicrosoftActivity: false,
            },
        },
        // site admin added: fraudulent IP. allowed IP.
        {
            severity: 'warning',
            description: (fields) =>
                `A suspicious IP within your allowed IP list added a site collection administrator`,
            longDescription: (fields) =>
                `A person was added as a site collection administrator on SharePoint. The action originated from a suspicious IP included in your allowed IP list. While the IP range is normally trusted, the specific IP has been flagged as suspicious, which makes the event unusual. Because site collection administrator rights provide broad control over SharePoint resources, this change should be reviewed to confirm whether it was legitimate or potentially unauthorized.`,
            predicates: {
                isFraudulentIP: true,
                isAllowedIP: true,
                isMicrosoftActivity: false,
            },
        },
        // site admin added: fraudulent IP. allowed region.
        {
            severity: 'warning',
            description: (fields) => `A suspicious IP within your allowed region added a site collection administrator`,
            longDescription: (fields) =>
                `A person was added as a site collection administrator on SharePoint. The action originated from a suspicious IP located in your allowed region. While the region itself is normally considered trusted, the IP has been flagged as suspicious, which makes the activity unusual. Because site collection administrator rights provide broad access and control over SharePoint resources, this change should be reviewed to confirm whether it was expected or potentially unauthorized.`,
            predicates: {
                isFraudulentIP: true,
                isAllowedRegion: true,
                isMicrosoftActivity: false,
            },
        },
        // site admin added: fraudulent IP. in allowed countries
        {
            severity: 'warning',
            description: (fields) =>
                `A suspicious IP within your allowed countries added a site collection administrator`,
            longDescription: (fields) =>
                `A person was added as a site collection administrator on SharePoint. The action originated from a suspicious IP located in one of your allowed countries. Although the country itself aligns with your access policies, the specific IP has been flagged as suspicious, making the change unusual. Because site collection administrator rights provide wide-reaching access and control over SharePoint resources, this assignment should be reviewed to confirm whether it was legitimate or potentially unauthorized.`,
            predicates: {
                isFraudulentIP: true,
                isAllowedCountry: true,
                isMicrosoftActivity: false,
            },
        },
        // site admin added: fraudulent IP. in allowed asn
        {
            severity: 'warning',
            description: (fields) =>
                `A suspicious IP within your allowed ASN list added a site collection administrator`,
            longDescription: (fields) =>
                `A person was added as a site collection administrator on SharePoint. The action originated from a suspicious IP belonging to your allowed ASN list. While the ASN itself is normally trusted, the individual IP has been flagged as suspicious, which makes the change unusual. Since site collection administrator rights grant broad access and control over SharePoint data and configuration, this assignment should be reviewed to confirm whether it was legitimate or potentially unauthorized.`,
            predicates: {
                isFraudulentIP: true,
                isAllowedASN: true,
                isMicrosoftActivity: false,
            },
        },
        // site admin added: not in allowed list.
        {
            severity: 'warning',
            description: (fields) => `An unapproved IP added a site collection administrator`,
            longDescription: (fields) =>
                `A person was added as a site collection administrator on SharePoint. The action originated from an unapproved IP outside your allowed list. Because site collection administrator rights provide broad control over SharePoint sites and data, this assignment is unusual and should be reviewed to confirm whether it was legitimate or potentially unauthorized.`,
            predicates: {
                isAllowedASN: false,
                isAllowedIP: false,
                isAllowedRegion: false,
                isAllowedCountry: false,
                isMicrosoftActivity: false,
            },
        },
        // site admin added: microsoft activity
        {
            severity: 'info',
            description: (fields) =>
                `A Microsoft system operation (synchronization server IP) added a site collection administrator`,
            longDescription: (fields) =>
                `A person was added as a site collection administrator on SharePoint. This event was recorded as a Microsoft system operation, with the IP address tied to the server responsible for synchronization. This entry reflects expected background activity and is informational in nature. No action is required unless system-generated changes appear inconsistent with your configuration or operational expectations.`,
            predicates: {
                isMicrosoftActivity: true,
            },
        },
        // site admin added
        {
            severity: 'info',
            description: (fields) => `A site collection administrator was added on SharePoint`,
            longDescription: (fields) =>
                `A person was added as a site collection administrator on SharePoint. This is an informational record of a standard administrative change. No unusual conditions were associated with this entry. No action is required unless the administrator assignment was unexpected or coincides with other unusual activity.`,
        },
    ],
};

export default SiteCollectionAdminAdded;
